import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { WorldVisibility } from "@townsy/contracts";
import { errorMessage } from "../../api/client";
import { putWorldSettings, useWorldSettingsQuery, worldKeys } from "../../api/worlds";
import { useTownStore } from "../../state/store";

/** The three choices, in the order shown (ADR-0043 decision 7; "People I've messaged" is the default). */
export const WORLD_VISIBILITY_CHOICES: readonly { value: WorldVisibility; label: string; done: string }[] = [
  { value: "messaged", label: "People I've messaged", done: "Only people you've sent a private message to can see which world you're in." },
  { value: "everyone", label: "Everyone", done: "Everyone in Townsy can see which world you're in." },
  { value: "nobody", label: "Nobody", done: "Nobody can see which world you're in now." },
];

/**
 * Account page (ADR-0043): "Who can see which world I'm in". The server enforces it on every lookup ("Find a person",
 * the person card) and every join ("Join them in Finch"); this card only shows and saves the choice.
 */
export function WorldVisibilityCard({ signedIn }: { signedIn: boolean }) {
  const q = useWorldSettingsQuery(signedIn);
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** The choice being saved: shown checked at once (a controlled radio would otherwise snap back until the server answers). */
  const [pending, setPending] = useState<WorldVisibility | null>(null);
  if (!signedIn || !q.data) return null;
  const current = pending ?? q.data.visibility;
  const choose = async (visibility: WorldVisibility) => {
    if (visibility === current) return;
    setPending(visibility);
    setBusy(true);
    setError(null);
    try {
      const res = await putWorldSettings(visibility);
      qc.setQueryData(worldKeys.settings, res);
      useTownStore.getState().pushToast(WORLD_VISIBILITY_CHOICES.find((c) => c.value === res.visibility)?.done ?? "Saved.", "success");
    } catch (err) {
      setError(errorMessage(err, "The setting was not saved."));
    } finally {
      setPending(null);
      setBusy(false);
    }
  };
  return <WorldVisibilityView current={current} busy={busy} error={error} onChoose={(v) => void choose(v)} />;
}

/** Presentational (server-rendered in the unit tests). */
export function WorldVisibilityView({ current, busy, error, onChoose }: { current: WorldVisibility; busy: boolean; error: string | null; onChoose(v: WorldVisibility): void }) {
  return (
    <div className="section" data-testid="account-world-visibility">
      <div className="section-title">Worlds</div>
      <fieldset className="choice-group" disabled={busy}>
        <legend className="small">Who can see which world I'm in</legend>
        {WORLD_VISIBILITY_CHOICES.map((c) => (
          <label key={c.value} className="choice">
            <input type="radio" name="world-visibility" checked={current === c.value} onChange={() => onChoose(c.value)} data-testid={`world-visibility-${c.value}`} />
            <span>{c.label}</span>
          </label>
        ))}
      </fieldset>
      <span className="hint">
        Townsy runs several copies of the town ("worlds") when it is busy. This decides who can find you with "Find a person" and "Join them". Muting someone
        always hides you from them.
      </span>
      {error ? (
        <div className="small error-text" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}
